import type { CSSProperties } from 'react'
import type { ImageFrame, ImagePlacement } from './types'

// Crop & position, non-destructively. An image is scaled to cover its box
// (like object-fit: cover), times `zoom`, and `focus` picks which part shows
// (object-position semantics: 0 = left/top edge, 1 = right/bottom). The file
// itself is never touched, so any framing can be redone or reset.
//
// The same maths renders everywhere — composer, reader, previews — through
// components/FramedImage.tsx, with CSS container units so it doesn't need to
// know the box's size: the box is `container-type: size` and the image sizes
// itself from 100cqw / 100cqh. The editor (components/ImageEditor.tsx) uses
// the pixel versions below to turn drags and zooms into a new frame.

export const MIN_ZOOM = 1
export const MAX_ZOOM = 4

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n))
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

/** A frame as stored, or null when it's missing or malformed (then the default framing applies). */
export function safeFrame(f: unknown): ImageFrame | null {
  if (!f || typeof f !== 'object') return null
  const o = f as Record<string, unknown>
  const natural = num(o.natural)
  const focus = o.focus as Record<string, unknown> | undefined
  const fx = num(focus?.x)
  const fy = num(focus?.y)
  if (natural === null || natural <= 0.05 || natural > 20 || fx === null || fy === null) return null
  const zoom = num(o.zoom) ?? 1
  const shape = num(o.shape)
  return {
    natural,
    focus: { x: clamp(fx, 0, 1), y: clamp(fy, 0, 1) },
    zoom: clamp(zoom, MIN_ZOOM, MAX_ZOOM),
    ...(shape !== null && shape > 0.05 && shape <= 20 ? { shape } : {}),
  }
}

export function defaultFrame(natural: number): ImageFrame {
  return { natural, focus: { x: 0.5, y: 0.5 }, zoom: 1 }
}

/** Nothing moved or zoomed (and no shape chosen): the same as no frame at all. */
export function isDefaultFrame(f: ImageFrame | null | undefined): boolean {
  return !f || (f.zoom === 1 && f.focus.x === 0.5 && f.focus.y === 0.5 && f.shape === undefined)
}

/** The box aspect for a shape-free placement: the chosen shape, else the image's own. */
export function shapeOf(f: ImageFrame): number {
  return f.shape ?? f.natural
}

/**
 * Styles for the <img> inside a box that is `position: relative;
 * overflow: hidden; container-type: size` — the image covers the box, zoomed,
 * aligned by focus. Numbers are clamped first, so stored values can't produce
 * anything but a position.
 */
export function framedImageStyle(frame: ImageFrame): CSSProperties {
  const f = safeFrame(frame) ?? defaultFrame(1)
  const a = css(f.natural)
  const z = css(f.zoom)
  const w = `(max(100cqw, 100cqh * ${a}) * ${z})`
  const h = `(max(100cqh, 100cqw / ${a}) * ${z})`
  return {
    position: 'absolute',
    maxWidth: 'none',
    width: `calc${w}`,
    height: `calc${h}`,
    left: `calc((100cqw - ${w}) * ${css(f.focus.x)})`,
    top: `calc((100cqh - ${h}) * ${css(f.focus.y)})`,
  }
}

/** A number as CSS accepts it: fixed-point, never "1e-7". */
function css(n: number): string {
  return String(Number(n.toFixed(4)))
}

/* ------------------------------------------------------------ free crop (full-width and inline images) */

/** An inline image's box before it's cropped: the column at 816×400, sitting at 'center 60%'. */
export const INLINE_BOX = 816 / 400
export const INLINE_REST_Y = 0.6

/**
 * How an image block's box is shaped. Full width is always the crop's shape
 * (the photo's own when uncropped). Inline takes its crop's shape once it has
 * been cropped freely, and is a steady-height band until then. Wraps are fixed.
 */
export function imageFit(placement: ImagePlacement | undefined, frame: ImageFrame | null | undefined): 'shape' | 'cover' {
  if (placement === 'full') return 'shape'
  if ((placement ?? 'inline') === 'inline' && safeFrame(frame)?.shape !== undefined) return 'shape'
  return 'cover'
}

/** A crop rectangle on the original image, 0..1 of its width and height. */
export interface CropRect {
  x: number
  y: number
  w: number
  h: number
}

export const FULL_CROP: CropRect = { x: 0, y: 0, w: 1, h: 1 }

/**
 * The smallest crop side (0..1): the frame's zoom tops out at MAX_ZOOM, and
 * zoom = 1 / the crop's longer side, so the longer side can't go below this.
 */
export const MIN_CROP_SIDE = 1 / MAX_ZOOM

/**
 * The rectangle a shape-free frame shows. A frame whose box takes its shape is
 * exactly a crop: at zoom 1 the image covers the box, zoom narrows the view,
 * focus slides it.
 */
export function frameToCrop(frame: ImageFrame | null | undefined): CropRect {
  const f = safeFrame(frame)
  if (!f) return { ...FULL_CROP }
  const ratio = shapeOf(f) / f.natural // the crop's w/h in 0..1 units
  const w = Math.min(1, ratio) / f.zoom
  const h = Math.min(1, 1 / ratio) / f.zoom
  return { x: f.focus.x * (1 - w), y: f.focus.y * (1 - h), w, h }
}

/** The frame that shows exactly `rect` of an image of aspect `natural`; null = the whole image. */
export function cropToFrame(rect: CropRect, natural: number): ImageFrame | null {
  const w = clamp(rect.w, 0, 1)
  const h = clamp(rect.h, 0, 1)
  const x = clamp(rect.x, 0, 1 - w)
  const y = clamp(rect.y, 0, 1 - h)
  if (w > 0.999 && h > 0.999) return null
  return {
    natural,
    shape: (w / h) * natural,
    zoom: clamp(1 / Math.max(w, h), MIN_ZOOM, MAX_ZOOM),
    focus: { x: w < 0.999 ? x / (1 - w) : 0.5, y: h < 0.999 ? y / (1 - h) : 0.5 },
  }
}

/**
 * The part of the photo a box of aspect `box` (width / height) shows now: the
 * frame's zoom and focus, or for an unframed photo the whole width at
 * `restY` (an inline band rests at 'center 60%').
 */
export function visibleCrop(natural: number, box: number, frame: ImageFrame | null | undefined, restY = 0.5): CropRect {
  const f = safeFrame(frame)
  return frameToCrop({ natural, shape: box, zoom: f?.zoom ?? 1, focus: f?.focus ?? { x: 0.5, y: restY } })
}

/** The shortest a box may be cropped to by the corner handle, in px. */
export const MIN_CROP_BOX = 80

/**
 * Crop with the corner handle (an image that fills its column, dragged
 * straight). The box is `box` px now and shows `crop`; it grows or shrinks
 * by `delta` px along `axis`, and the crop follows so the photo keeps its
 * scale: up and down ('y') moves the bottom edge, so the top stays put;
 * sideways ('x') trims both sides evenly, since the image is centred —
 * unless `keep` says which side stays put: a wrap is pinned to its outer
 * edge, so only the side its corner is on moves ('left' for a wrap-left,
 * whose corner is bottom-right; 'right' for a wrap-right). It can't uncrop
 * past the photo's edges, or grow past `max` px (the column's width, or the
 * widest a wrap goes), or crop below `min` px, MIN_CROP_BOX px or what a
 * frame can zoom to. Returns the new crop and the box's new length along
 * that axis.
 */
export function cropAlong(
  axis: 'x' | 'y',
  crop: CropRect,
  box: Box,
  delta: number,
  max = Infinity,
  { keep, min = 0 }: { keep?: 'left' | 'right'; min?: number } = {},
): { crop: CropRect; length: number } {
  const length0 = axis === 'x' ? box.width : box.height
  const side0 = axis === 'x' ? crop.w : crop.h
  if (!(length0 > 0 && side0 > 0)) return { crop, length: length0 }
  const perUnit = length0 / side0 // px per unit of the photo along the axis
  const other = axis === 'x' ? crop.h : crop.w
  // A frame's zoom is 1 / the crop's longer side, so the longer side can't go below MIN_CROP_SIDE.
  const minSide = other >= MIN_CROP_SIDE ? 0 : MIN_CROP_SIDE
  const lo = Math.max(MIN_CROP_BOX, min, minSide * perUnit)
  const hi = Math.min(max, perUnit) // perUnit px is the whole photo along this axis
  const length = clamp(length0 + delta, Math.min(lo, length0), Math.max(hi, Math.min(lo, length0)))
  const side = clamp(length / perUnit, 0, 1)
  if (axis === 'y') {
    const y = clamp(crop.y, 0, 1 - side)
    return { crop: { ...crop, y, h: side }, length }
  }
  const from = keep === 'left' ? crop.x : keep === 'right' ? crop.x + crop.w - side : crop.x + (crop.w - side) / 2
  const x = clamp(from, 0, 1 - side)
  return { crop: { ...crop, x, w: side }, length }
}

/* ------------------------------------------------------------ pixel maths (the editor) */

export interface Box {
  width: number
  height: number
}

/** The image's rendered size and top-left offset inside a box, in px. */
export function layoutIn(frame: ImageFrame, box: Box): { width: number; height: number; left: number; top: number } {
  const a = frame.natural
  const width = Math.max(box.width, box.height * a) * frame.zoom
  const height = Math.max(box.height, box.width / a) * frame.zoom
  return { width, height, left: (box.width - width) * frame.focus.x, top: (box.height - height) * frame.focus.y }
}

/** Move the image by (dx, dy) px, as a drag would: the image follows the pointer. */
export function panBy(frame: ImageFrame, box: Box, dx: number, dy: number): ImageFrame {
  const { width, height } = layoutIn(frame, box)
  const ox = width - box.width
  const oy = height - box.height
  return {
    ...frame,
    focus: {
      x: ox > 0.5 ? clamp(frame.focus.x - dx / ox, 0, 1) : frame.focus.x,
      y: oy > 0.5 ? clamp(frame.focus.y - dy / oy, 0, 1) : frame.focus.y,
    },
  }
}

/**
 * Zoom to `zoom`, keeping the image point under `anchor` (px within the box;
 * default its centre) where it is — the way a pinch or a wheel feels.
 */
export function zoomTo(frame: ImageFrame, box: Box, zoom: number, anchor?: { x: number; y: number }): ImageFrame {
  const z = clamp(zoom, MIN_ZOOM, MAX_ZOOM)
  const ax = anchor?.x ?? box.width / 2
  const ay = anchor?.y ?? box.height / 2
  const before = layoutIn(frame, box)
  // The image point (0..1) under the anchor now…
  const u = (ax - before.left) / before.width
  const v = (ay - before.top) / before.height
  const next = { ...frame, zoom: z }
  const after = layoutIn(next, box)
  const ox = after.width - box.width
  const oy = after.height - box.height
  // …stays under it after zooming: left' = ax - u * width' = -ox * fx'.
  return {
    ...next,
    focus: {
      x: ox > 0.5 ? clamp((u * after.width - ax) / ox, 0, 1) : 0.5,
      y: oy > 0.5 ? clamp((v * after.height - ay) / oy, 0, 1) : 0.5,
    },
  }
}
